<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PrivacyOperationsAPI from 'dashboard/api/privacyOperations';
import SectionLayout from '../../account/components/SectionLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisActionReasonModal from 'dashboard/components-next/LaisActionReasonModal.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';

const { t } = useI18n();

const contactId = ref('');
const snapshot = ref(null);
const lastDryRunReceipt = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const dryRunError = ref('');
const showDryRunModal = ref(false);
const permissionDenied = ref(false);

const trimmedContactId = computed(() => String(contactId.value || '').trim());
const hasSnapshot = computed(() => Boolean(snapshot.value));
const subjectEvidence = computed(() => snapshot.value?.subject_evidence);
const hasSubjectEvidence = computed(() => Boolean(subjectEvidence.value));
const retentionPreview = computed(
  () => snapshot.value?.retention_preview || {}
);
const retentionResources = computed(
  () => retentionPreview.value.resources || []
);
const recentRequests = computed(
  () => snapshot.value?.recent_erasure_requests || []
);
const inventory = computed(() => subjectEvidence.value?.inventory || {});
const exportPreview = computed(() => subjectEvidence.value?.export || {});
const erasurePreview = computed(() => subjectEvidence.value?.erasure || {});
const legalHold = computed(() => subjectEvidence.value?.legal_hold || {});
const resourcesWithData = computed(() =>
  (inventory.value.resources || []).filter(resource => resource.count > 0)
);
const retentionEligibleTotal = computed(() =>
  retentionResources.value.reduce(
    (total, resource) => total + Number(resource.eligible_count || 0),
    0
  )
);
const canRunDryRun = computed(
  () =>
    hasSubjectEvidence.value &&
    trimmedContactId.value.length > 0 &&
    !isSubmitting.value &&
    !permissionDenied.value
);

const summaryItems = computed(() => [
  {
    key: 'resources',
    label: t('SECURITY_SETTINGS.LGPD_OPERATIONS.SUMMARY.RESOURCES'),
    value: hasSubjectEvidence.value
      ? inventory.value.resources_with_data || 0
      : '-',
  },
  {
    key: 'records',
    label: t('SECURITY_SETTINGS.LGPD_OPERATIONS.SUMMARY.EXPORT_RECORDS'),
    value: hasSubjectEvidence.value
      ? exportPreview.value.record_count || 0
      : '-',
  },
  {
    key: 'retention',
    label: t('SECURITY_SETTINGS.LGPD_OPERATIONS.SUMMARY.RETENTION_ELIGIBLE'),
    value: retentionEligibleTotal.value,
  },
  {
    key: 'requests',
    label: t('SECURITY_SETTINGS.LGPD_OPERATIONS.SUMMARY.REQUESTS'),
    value: recentRequests.value.length,
  },
  {
    key: 'hold',
    label: t('SECURITY_SETTINGS.LGPD_OPERATIONS.SUMMARY.LEGAL_HOLD'),
    value: legalHold.value.active
      ? t('SECURITY_SETTINGS.LGPD_OPERATIONS.STATUS.ACTIVE')
      : t('SECURITY_SETTINGS.LGPD_OPERATIONS.STATUS.CLEAR'),
  },
]);

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const requestTitle = request => `#${request.id} - ${request.status}`;

const booleanLabel = value =>
  value
    ? t('SECURITY_SETTINGS.LGPD_OPERATIONS.STATUS.YES')
    : t('SECURITY_SETTINGS.LGPD_OPERATIONS.STATUS.NO');

const errorFromResponse = (error, fallbackMessage) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    permissionDenied.value = true;
    return t('SECURITY_SETTINGS.LGPD_OPERATIONS.PERMISSION_DENIED');
  }

  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    fallbackMessage
  );
};

const statusParams = () =>
  trimmedContactId.value ? { contact_id: trimmedContactId.value } : {};

const loadStatus = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    dryRunError.value = '';
    permissionDenied.value = false;
    const response =
      await PrivacyOperationsAPI.operationalStatus(statusParams());
    snapshot.value = response?.data?.payload || null;
  } catch (error) {
    errorMessage.value = errorFromResponse(
      error,
      t('SECURITY_SETTINGS.LGPD_OPERATIONS.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const openDryRunModal = () => {
  if (!canRunDryRun.value) return;
  dryRunError.value = '';
  showDryRunModal.value = true;
};

const closeDryRunModal = () => {
  if (isSubmitting.value) return;
  showDryRunModal.value = false;
};

const createDryRun = async ({ reason }) => {
  if (!canRunDryRun.value) return;

  try {
    isSubmitting.value = true;
    dryRunError.value = '';
    const response = await PrivacyOperationsAPI.createErasureDryRun({
      contactId: trimmedContactId.value,
      reason,
    });
    lastDryRunReceipt.value = response?.data?.payload || null;
    useAlert(t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.SUCCESS'));
    showDryRunModal.value = false;
    await loadStatus();
  } catch (error) {
    dryRunError.value = errorFromResponse(
      error,
      t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(loadStatus);
</script>

<template>
  <SectionLayout
    :title="t('SECURITY_SETTINGS.LGPD_OPERATIONS.TITLE')"
    :description="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DESCRIPTION')"
    with-border
  >
    <template #headerActions>
      <Button
        v-tooltip.top="t('SECURITY_SETTINGS.LGPD_OPERATIONS.REFRESH')"
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="ghost"
        size="sm"
        :is-loading="isLoading"
        @click="loadStatus"
      />
    </template>

    <div class="grid gap-4" data-testid="lgpd-operations-panel">
      <form
        class="grid gap-3 rounded-md border border-n-weak bg-n-alpha-1 p-4 lg:grid-cols-[minmax(180px,260px)_auto]"
        data-testid="lgpd-contact-form"
        @submit.prevent="loadStatus"
      >
        <label class="grid gap-1 text-sm font-medium text-n-slate-12">
          {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.CONTACT_ID') }}
          <input
            v-model="contactId"
            class="h-10 rounded-md border border-n-weak bg-white px-3 text-sm text-n-slate-12"
            type="number"
            min="1"
            inputmode="numeric"
            data-testid="lgpd-contact-id"
          />
        </label>
        <div class="flex items-end gap-2">
          <Button
            icon="i-lucide-search"
            color="slate"
            size="sm"
            type="submit"
            :is-loading="isLoading"
            :label="t('SECURITY_SETTINGS.LGPD_OPERATIONS.LOAD_SUBJECT')"
            data-testid="lgpd-load-subject"
          />
          <Button
            icon="i-lucide-shield-check"
            color="ruby"
            size="sm"
            type="button"
            :disabled="!canRunDryRun"
            :is-loading="isSubmitting"
            :label="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.SUBMIT')"
            data-testid="lgpd-dry-run-open"
            @click="openDryRunModal"
          />
        </div>
      </form>

      <div
        v-if="isLoading && !hasSnapshot"
        class="rounded-md border border-n-weak bg-n-alpha-1 px-4 py-8 text-center text-sm text-n-slate-11"
      >
        {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.LOADING') }}
      </div>

      <div
        v-else-if="errorMessage"
        class="rounded-md border border-n-ruby-8 bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
        data-testid="lgpd-error"
      >
        {{ errorMessage }}
      </div>

      <template v-else-if="hasSnapshot">
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div
            v-for="item in summaryItems"
            :key="item.key"
            class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-3"
          >
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ item.label }}
            </div>
            <div class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ item.value }}
            </div>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-md border border-n-weak bg-n-alpha-1 p-4 lg:grid-cols-3"
        >
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.HEADERS.VERSION') }}
            </div>
            <div class="mt-1 break-all text-sm font-medium text-n-slate-12">
              {{ snapshot.version }}
            </div>
          </div>
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.HEADERS.GENERATED_AT') }}
            </div>
            <div class="mt-1 text-sm font-medium text-n-slate-12">
              {{ formatDate(snapshot.generated_at) }}
            </div>
          </div>
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.HEADERS.WRITES') }}
            </div>
            <div class="mt-1 text-sm font-medium text-n-slate-12">
              {{ booleanLabel(snapshot.destructive_actions_enabled) }}
            </div>
          </div>
        </div>

        <div
          v-if="hasSubjectEvidence"
          class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
        >
          <div class="grid gap-4">
            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.TITLE') }}
              </h4>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <div class="text-xs font-medium uppercase text-n-slate-10">
                    {{
                      t('SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.SUBJECT')
                    }}
                  </div>
                  <div
                    class="mt-1 break-all text-sm font-semibold text-n-slate-12"
                    data-testid="lgpd-subject-digest"
                  >
                    {{ subjectEvidence.subject.identity_digest }}
                  </div>
                </div>
                <div>
                  <div class="text-xs font-medium uppercase text-n-slate-10">
                    {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.EXPORT') }}
                  </div>
                  <div
                    class="mt-1 break-all text-sm font-semibold text-n-slate-12"
                  >
                    {{ exportPreview.digest }}
                  </div>
                </div>
              </div>

              <div class="mt-4 overflow-hidden rounded-md border border-n-weak">
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-n-weak text-sm">
                    <thead
                      class="bg-n-alpha-1 text-left text-xs uppercase text-n-slate-10"
                    >
                      <tr>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.TABLE.RESOURCE'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.TABLE.COUNT'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.TABLE.RETENTION'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.TABLE.HOLD'
                            )
                          }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak bg-white">
                      <tr
                        v-for="resource in resourcesWithData"
                        :key="resource.key"
                      >
                        <td class="px-3 py-2 font-medium text-n-slate-12">
                          {{ resource.key }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{ resource.count }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{
                            resource.retention?.default_retention_days ||
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.NO_WINDOW'
                            )
                          }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{ booleanLabel(resource.legal_hold?.active) }}
                        </td>
                      </tr>
                      <tr v-if="resourcesWithData.length === 0">
                        <td
                          colspan="4"
                          class="px-3 py-6 text-center text-sm text-n-slate-11"
                        >
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.MANIFEST.EMPTY'
                            )
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.RETENTION.TITLE') }}
              </h4>
              <div class="mt-3 overflow-hidden rounded-md border border-n-weak">
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-n-weak text-sm">
                    <thead
                      class="bg-n-alpha-1 text-left text-xs uppercase text-n-slate-10"
                    >
                      <tr>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.RETENTION.TABLE.RESOURCE'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.RETENTION.TABLE.STATUS'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.RETENTION.TABLE.ELIGIBLE'
                            )
                          }}
                        </th>
                        <th class="px-3 py-2">
                          {{
                            t(
                              'SECURITY_SETTINGS.LGPD_OPERATIONS.RETENTION.TABLE.APPLIED'
                            )
                          }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak bg-white">
                      <tr
                        v-for="resource in retentionResources"
                        :key="resource.key"
                      >
                        <td class="px-3 py-2 font-medium text-n-slate-12">
                          {{ resource.key }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{ resource.status }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{ resource.eligible_count || 0 }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11">
                          {{ resource.applied_count || 0 }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="grid content-start gap-4">
            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.ERASURE.TITLE') }}
              </h4>
              <dl class="mt-3 grid gap-3 text-sm">
                <div>
                  <dt class="text-xs font-medium uppercase text-n-slate-10">
                    {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.ERASURE.ALLOWED') }}
                  </dt>
                  <dd class="mt-1 font-semibold text-n-slate-12">
                    {{ booleanLabel(erasurePreview.allowed_now) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-medium uppercase text-n-slate-10">
                    {{
                      t('SECURITY_SETTINGS.LGPD_OPERATIONS.ERASURE.LEGAL_HOLD')
                    }}
                  </dt>
                  <dd class="mt-1 text-n-slate-11">
                    {{
                      legalHold.active
                        ? legalHold.reasons?.join(', ')
                        : t('SECURITY_SETTINGS.LGPD_OPERATIONS.STATUS.CLEAR')
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-medium uppercase text-n-slate-10">
                    {{
                      t('SECURITY_SETTINGS.LGPD_OPERATIONS.ERASURE.STRATEGY')
                    }}
                  </dt>
                  <dd class="mt-1 text-n-slate-11">
                    {{ erasurePreview.execution_strategy }}
                  </dd>
                </div>
              </dl>
              <div
                v-if="dryRunError"
                class="mt-3 rounded-md border border-n-ruby-8 bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
                data-testid="lgpd-dry-run-error"
              >
                {{ dryRunError }}
              </div>
              <Button
                class="mt-4"
                icon="i-lucide-file-check-2"
                color="ruby"
                size="sm"
                type="button"
                :disabled="!canRunDryRun"
                :is-loading="isSubmitting"
                :label="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.SUBMIT')"
                @click="openDryRunModal"
              />
            </div>

            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.REQUESTS.TITLE') }}
              </h4>
              <div
                v-if="recentRequests.length === 0"
                class="mt-3 rounded-md border border-n-weak bg-white px-3 py-3 text-sm text-n-slate-11"
                data-testid="lgpd-requests-empty"
              >
                {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.REQUESTS.EMPTY') }}
              </div>
              <div v-else class="mt-3 grid gap-2">
                <div
                  v-for="request in recentRequests"
                  :key="request.id"
                  class="rounded-md border border-n-weak bg-white px-3 py-2 text-sm"
                >
                  <div class="flex items-center justify-between gap-2">
                    <span class="font-semibold text-n-slate-12">
                      {{ requestTitle(request) }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ request.mode }}
                    </span>
                  </div>
                  <div class="mt-1 text-xs text-n-slate-10">
                    {{ formatDate(request.requested_at) }}
                  </div>
                </div>
              </div>
            </div>

            <LaisPayloadViewer
              v-if="lastDryRunReceipt"
              :title="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.RECEIPT')"
              :payload="lastDryRunReceipt"
              :collapsed="false"
              data-testid="lgpd-dry-run-receipt"
            />
          </div>
        </div>

        <div
          v-else
          class="rounded-md border border-n-weak bg-n-alpha-1 px-4 py-8 text-center text-sm text-n-slate-11"
          data-testid="lgpd-no-subject"
        >
          {{ t('SECURITY_SETTINGS.LGPD_OPERATIONS.NO_SUBJECT') }}
        </div>
      </template>
    </div>

    <LaisActionReasonModal
      :show="showDryRunModal"
      :title="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.MODAL_TITLE')"
      :description="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.MODAL_DESCRIPTION')
      "
      :consequence="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.MODAL_CONSEQUENCE')
      "
      :reason-label="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.REASON')"
      :reason-placeholder="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.REASON_PLACEHOLDER')
      "
      :acknowledge-label="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.ACKNOWLEDGE')
      "
      confirmation-phrase="DRY-RUN"
      :confirmation-label="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.CONFIRMATION')
      "
      :confirmation-placeholder="
        t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.CONFIRMATION_PLACEHOLDER')
      "
      :confirm-text="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.CONFIRM')"
      :cancel-text="t('SECURITY_SETTINGS.LGPD_OPERATIONS.DRY_RUN.CANCEL')"
      confirm-variant="danger"
      :is-loading="isSubmitting"
      @close="closeDryRunModal"
      @confirm="createDryRun"
    />
  </SectionLayout>
</template>
